import { useCallback, useEffect, useState } from 'react';
import { get } from '@/utils';

// ==============================|| USER STATUSES - HOOK REUTILIZABIL ||============================== //
// GET api/user-statuses → statusurile de utilizator (Activ / Blocat / Restricționat).

export interface IUserStatus {
	id: number;
	name: string;
	description?: string | null;
}

interface UserStatusesResponse {
	items?: Array<{ id?: number; name?: string; description?: string | null }>;
}

export type UserStatusKey = 'active' | 'blocked' | 'muted';

// Numele statusurilor vin din BE (RO/EN) → le mapăm la o cheie stabilă.
const STATUS_KEY_MAP: Record<string, UserStatusKey> = {
	activ: 'active',
	active: 'active',
	blocat: 'blocked',
	blocked: 'blocked',
	restrictionat: 'muted',
	restricționat: 'muted',
	muted: 'muted'
};

// Rezolvă id-ul de status după cheie (ex. 'blocked' → id-ul „Blocat"), pentru PATCH .../status.
export const getStatusIdByKey = (
	statuses: IUserStatus[],
	key: UserStatusKey
): number | undefined =>
	statuses.find((status) => STATUS_KEY_MAP[status.name.trim().toLowerCase()] === key)?.id;

export function useUserStatuses(fetchOnRender = true): {
	statuses: IUserStatus[];
	loading: boolean;
	getUserStatuses: () => Promise<void>;
} {
	const [statuses, setStatuses] = useState<IUserStatus[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const getUserStatuses = useCallback(async () => {
		try {
			setLoading(true);
			const res = await get('api/user-statuses?PageNumber=1&PageSize=100');
			const data = res.data as UserStatusesResponse;
			const items = Array.isArray(data.items) ? data.items : [];
			setStatuses(
				items
					.map((s) => ({
						id: Number(s.id || 0),
						name: String(s.name || ''),
						description: s.description ?? null
					}))
					.filter((s) => s.name)
			);
		} catch {
			setStatuses([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (fetchOnRender) {
			getUserStatuses();
		}
	}, [fetchOnRender, getUserStatuses]);

	return { statuses, loading, getUserStatuses };
}
